import { useState, useMemo, useRef, useEffect, type ReactElement } from "react";

import { DialogTrigger, Popover, Dialog } from "react-aria-components";

import { Button } from "#components/Button";
import { useScrollEnd } from "#hooks";
import { ChevronDownIcon } from "#icons";
import { cn } from "#utils";

import { ColumnContent } from "./columns/ColumnContent";
import { convertSelectionsToCriteriaFilterValue } from "./utils";

import type { Column } from "./columns/types";

export type SteppedMultiColumnDropdownConfig = Column[][];

export interface CriteriaFilterValue {
  /** The criteria type (e.g., "account", "account-tag", "resource-tag") */
  criteria: string;
  /** The name field (may differ from criteria for tag types) */
  name: string;
  /** The operator (e.g., "equals", "contains") */
  operator?: string;
  /** Array of selected values with id and name */
  value?: { id: string; name: string }[];
}

// Props for the main component
export interface SteppedMultiColumnDropdownProps {
  /** The model configuration for columns */
  config: SteppedMultiColumnDropdownConfig;
  /**
   * Callback when selection changes.
   * Only called when all fields in the current path are fulfilled.
   * When changing earlier selections, subsequent selections are preserved if still valid.
   * The popover only closes when changing the last column.
   * Used primarily for jira dropdowns and any simpler dropdowns that don't need the structured data.
   */
  onChange?: (selections: string[]) => void;
  /**
   * Structured callback when selection changes.
   * Provides structured data with criteria, name, operator, and value as an array of objects with id and name properties.
   * Only called when all fields in the current path are fulfilled.
   * Used primarily for policy criteria dropdowns and any dropdowns that need the structured data.
   */
  onChangeStructured?: (data: CriteriaFilterValue) => void;
  /** Initial selections */
  initialSelections?: string[];
  /**
   * Custom trigger element or render function.
   * If a render function is provided, it receives an object with `isOpen` state.
   * If not provided, a default button with "Criteria dropdown" text will be used.
   */
  trigger?: ReactElement | ((props: { isOpen: boolean }) => ReactElement);
  // If true, the dropdown will be as tall as the content
  autoHeight?: boolean;
  /**
   * Callback when search input changes in any column.
   * Receives the search term, the index of the column being searched, and current selections.
   */
  onSearch?: (searchTerm: string, columnIndex: number, selections?: string[]) => void;
  /**
   * Callback when a column's scrollbar reaches the end.
   * Useful for implementing pagination/infinite scroll.
   * Receives the index of the column that was scrolled and current selections.
   */
  onScrollEnd?: (columnIndex: number, selections?: string[]) => void;
  /**
   * Callback when the popover open state changes for any reason,
   * including opening, user dismissal, and completed selection.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /** Additional CSS classes applied to the trigger wrapper element */
  className?: string;
}

/**
 * Buffer time in milliseconds to ensure debounced search callbacks complete.
 * The debounce period for search is typically 300ms, so we use 350ms to
 * ensure completion with a safety buffer when staggering multiple search calls.
 */
const SEARCH_DEBOUNCE_BUFFER_MS = 350;

const resolveSelectionToColumnId = (value: string, column: Column | undefined): string => {
  if (!value || !column) {
    return value;
  }

  if (
    (column.type === "checkbox" || column.type === "list" || column.type === "search") &&
    column.items
  ) {
    if (column.items.some((item) => item.id === value)) {
      return value;
    }
    const item = column.items.find((entry) => entry.name === value);
    return item?.id ?? value;
  }

  return value;
};

const mapSelectionsToColumnIds = (
  rawSelections: string[],
  config: SteppedMultiColumnDropdownConfig,
): string[] => {
  if (rawSelections.length === 0) {
    return rawSelections;
  }

  const mappedSelections = [...rawSelections];

  config.forEach((columnGroup, groupIndex) => {
    const activeColumn = columnGroup.find((column) => {
      if (!column.condition) {
        return true;
      }
      return column.condition(mappedSelections);
    });

    if (!activeColumn) {
      return;
    }

    const selection = mappedSelections[groupIndex];
    if (!selection) {
      return;
    }

    if (activeColumn.type === "checkbox") {
      mappedSelections[groupIndex] = selection
        .split(",")
        .filter(Boolean)
        .map((value) => resolveSelectionToColumnId(value, activeColumn))
        .join(",");
      return;
    }

    mappedSelections[groupIndex] = resolveSelectionToColumnId(selection, activeColumn);
  });

  return mappedSelections;
};

export function SteppedMultiColumnDropdown({
  config,
  onChange,
  onChangeStructured,
  initialSelections = [],
  trigger,
  autoHeight = false,
  onSearch,
  onScrollEnd,
  onOpenChange,
  className,
}: SteppedMultiColumnDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selections, setSelections] = useState<string[]>(() =>
    mapSelectionsToColumnIds(initialSelections, config),
  );
  const [maxWidth, setMaxWidth] = useState(100);
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const columnScrollRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchResetTimeoutIds = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Determine which columns to show based on conditions
  const activeColumns = useMemo(() => {
    const columns: Column[] = [];

    config.forEach((columnGroup) => {
      // Find the first column in the group whose condition is met
      const activeColumn = columnGroup.find((column) => {
        if (!column.condition) {
          return true;
        }
        return column.condition(selections);
      });

      if (activeColumn) {
        columns.push(activeColumn);
      }
    });

    return columns;
  }, [config, selections]);

  // Measure trigger width
  useEffect(() => {
    if (triggerRef.current) {
      const triggerWidth = triggerRef.current.offsetWidth;

      setMaxWidth(triggerWidth);
    }
  }, [trigger]);

  // Cleanup pending search reset timeouts on unmount
  useEffect(() => {
    return () => {
      searchResetTimeoutIds.current.forEach(clearTimeout);
    };
  }, []);

  // Set up scroll end listeners for all columns
  const handleScrollEnd = useMemo(
    () =>
      onScrollEnd && isOpen
        ? (columnIndex?: number) => {
            if (columnIndex !== undefined) {
              onScrollEnd(columnIndex, selections);
            }
          }
        : undefined,
    [onScrollEnd, selections, isOpen],
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelections(mapSelectionsToColumnIds(initialSelections, config));
      setSearchTerms({});
    }
    onOpenChange?.(open);
  };

  useScrollEnd(columnScrollRefs, handleScrollEnd, {
    deps: [activeColumns, isOpen],
  });

  // Wrapper for onSearch to store the search term per column
  const handleSearch = (term: string, columnIndex: number) => {
    setSearchTerms((prev) => ({ ...prev, [columnIndex]: term }));
    onSearch?.(term, columnIndex, selections);
  };

  // Handle selection for a specific column
  const handleSelection = (columnIndex: number, value: string) => {
    const newSelections = [...selections];

    // Update the selection for the current column
    newSelections[columnIndex] = value;

    // Reset all search terms when criteria (first column) changes
    if (columnIndex === 0 && selections[0] !== value) {
      setSearchTerms({});

      // Cancel any pending search reset timeouts to prevent race conditions
      searchResetTimeoutIds.current.forEach(clearTimeout);
      searchResetTimeoutIds.current = [];

      // Trigger onSearch with empty string to refetch data for all affected columns
      // This ensures data is reset when switching between criteria
      // We need to determine which columns will be active for the new criteria
      // and trigger onSearch for those columns with staggered delays to avoid debounce conflicts
      let searchCallDelay = 0;
      config.forEach((columnGroup, groupIndex) => {
        const activeColumn = columnGroup.find((column) => {
          if (!column.condition) return true;
          return column.condition(newSelections);
        });

        // Check if active column is a search type
        const isActiveSearchColumn =
          activeColumn && (activeColumn.type === "search" || activeColumn.type === "enter-value");

        // Also check if ANY column in this group is a search type
        // (needed when conditions aren't met yet, e.g., operator not selected)
        const hasSearchColumn = columnGroup.some(
          (column) => column.type === "search" || column.type === "enter-value",
        );

        // Trigger onSearch if the active column is a search type, OR if the group
        // has search columns but none are active yet (conditions not met)
        if (isActiveSearchColumn || (!activeColumn && hasSearchColumn)) {
          // Stagger calls to ensure each onSearch completes its debounce period
          const timeoutId = setTimeout(() => {
            onSearch?.("", groupIndex, newSelections);
          }, searchCallDelay);
          searchResetTimeoutIds.current.push(timeoutId);
          searchCallDelay += SEARCH_DEBOUNCE_BUFFER_MS;
        }
      });
    }

    // Smart selection preservation: Only clear subsequent selections if they're no longer valid
    if (selections[columnIndex] !== value) {
      // Check each subsequent column to see if it's still valid with the new selection
      let currentValidColumns = columnIndex + 1;

      for (let groupIndex = columnIndex + 1; groupIndex < config.length; groupIndex++) {
        const columnGroup = config[groupIndex];

        // Find if any column in this group is still valid with the new selections
        const validColumn = columnGroup.find((column) => {
          if (!column.condition) return true;
          return column.condition(newSelections);
        });

        if (validColumn) {
          // Get the previously active column at this position to check if it's the same
          const previousColumns: Column[] = [];
          config.forEach((group) => {
            const prevCol = group.find((col) => {
              if (!col.condition) return true;
              return col.condition(selections); // Use old selections
            });
            if (prevCol) previousColumns.push(prevCol);
          });

          const previousColumn = previousColumns[currentValidColumns];
          const currentSelection = newSelections[currentValidColumns];

          if (currentSelection && previousColumn) {
            // Only preserve the value if it's the same column (same type and title)
            if (
              validColumn.type === previousColumn.type &&
              validColumn.title === previousColumn.title
            ) {
              // For list and search columns, check if the selected item still exists
              if (
                (validColumn.type === "list" || validColumn.type === "search") &&
                validColumn.items
              ) {
                const itemStillExists = validColumn.items.some(
                  (item) => item.id === currentSelection,
                );
                if (!itemStillExists) {
                  // Clear this selection as it's no longer valid
                  newSelections[currentValidColumns] = "";
                }
              }
              // For enter-value columns, the value is preserved since it's the same column
            } else {
              // Different column, clear the selection
              newSelections[currentValidColumns] = "";
            }
          }
          currentValidColumns++;
        } else {
          // No valid column in this group, clear any selection at this position
          if (currentValidColumns < newSelections.length) {
            newSelections[currentValidColumns] = "";
          }
        }
      }

      // Clear any remaining selections beyond valid columns
      for (let i = currentValidColumns; i < newSelections.length; i++) {
        newSelections[i] = "";
      }
    }

    setSelections(newSelections);

    // Determine new active columns based on the new selections
    const newActiveColumns: Column[] = [];
    config.forEach((columnGroup) => {
      const activeColumn = columnGroup.find((column) => {
        if (!column.condition) return true;
        return column.condition(newSelections);
      });
      if (activeColumn) {
        newActiveColumns.push(activeColumn);
      }
    });

    // Check if all active columns have been filled
    const allFilled = newActiveColumns.every(
      (_, index) => newSelections[index] && newSelections[index].trim() !== "",
    );

    // Only close the popover when changing the last column and all fields are filled
    const isLastColumn = columnIndex === newActiveColumns.length - 1;

    if (allFilled) {
      const finalSelections = newSelections.slice(0, newActiveColumns.length);

      // Call the onChange callback (for simpler dropdowns like Jira)
      onChange?.(finalSelections);

      // Call the structured onChange callback
      const structuredData = convertSelectionsToCriteriaFilterValue(
        finalSelections,
        newActiveColumns,
      );
      if (structuredData) {
        onChangeStructured?.(structuredData);
      }

      // But only close if it's the last column being changed AND it's not a checkbox column
      const lastColumn = newActiveColumns[newActiveColumns.length - 1];
      const isCheckboxColumn = lastColumn?.type === "checkbox";

      if (isLastColumn && !isCheckboxColumn) {
        handleOpenChange(false);
      }
    }
  };

  // Render the trigger element
  const renderTrigger = () => {
    if (trigger) {
      if (typeof trigger === "function") {
        return trigger({ isOpen });
      }
      // Otherwise, it's a ReactElement, return it as-is
      return trigger;
    }

    return (
      <Button
        IconRight={
          <ChevronDownIcon className={cn("text-gray-600", isOpen ? "rotate-180" : "")} size={16} />
        }
        className="w-[50rem] justify-between border-legacy-navy-L50 font-normal text-legacy-neutrals-ash"
        variant="tertiary"
      >
        Select Criteria
      </Button>
    );
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <span className={cn("inline-flex", className)} ref={triggerRef}>
        {renderTrigger()}
      </span>
      <Popover placement="bottom start">
        <Dialog
          className={cn(
            "flex overflow-hidden rounded-xs border border-outline-decorative bg-background-secondary py-2 shadow-lg",
            autoHeight ? "max-h-[20.25rem]" : "h-[20.25rem]",
          )}
          style={{ width: `${maxWidth}px` }}
        >
          {activeColumns.map((column, index) => (
            <div
              className={cn(
                "flex min-w-[11.25rem] flex-col",
                column.flex ? "flex-1" : "max-w-[11.25rem]",
              )}
              key={`${column.title}-${index}`}
            >
              <h3 className="px-4 py-2 text-body-small text-text-secondary">{column.title}</h3>
              <div
                className="flex-1 overflow-y-auto"
                ref={(el) => {
                  columnScrollRefs.current[index] = el;
                }}
              >
                <ColumnContent
                  column={column}
                  onSearch={(term) => handleSearch(term, index)}
                  onSelect={(value) => handleSelection(index, value)}
                  searchTerm={searchTerms[index] || ""}
                  selectedValue={selections[index]}
                />
              </div>
              {column.cta ? (
                <div className="text-text-link px-4 py-2 text-body-small">{column.cta}</div>
              ) : null}
            </div>
          ))}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
